/* eslint-disable import/order */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronLeft, Loader2, AlertCircle } from "lucide-react";

import { authApi } from "../../utils/axios";
import { config } from "../../utils/config";
import TeamBadge from "../../components/TeamBadge/TeamBadge";
import StatisticsTab from "../../components/TeamStats/StatisticsTab";
import PlayersTab from "../../components/TeamStats/PlayersTab";
/* eslint-enable import/order */

/**
 * Maps API stats response to the shape StatisticsTab expects
 */
const mapApiStatsToUiStats = (api) => {
  if (!api) return null;

  const goalDiff = (scored, conceded) => {
    const diff = (scored || 0) - (conceded || 0);
    return diff >= 0 ? `+${diff}` : `${diff}`;
  };

  return {
    matches:     api.games || 0,
    goals:       api.goalsScored || 0,
    yellowCards: api.yellowCards || 0,
    cleanSheets: api.cleanSheets || 0,
    assists:     api.assists || 0,
    redCards:    api.redCards || 0,
    averageGPG: {
      total: api.avgGoals || api.avgGoalsPerMatch || api.goalsPerGame || 0,
      home:  api.avgGoalsHome || api.avgGoalsPerMatchHome || 0,
      away:  api.avgGoalsAway || api.avgGoalsPerMatchAway || 0,
    },
    winLossRecord: {
      total:        api.games || 0,
      totalMatches: api.games || 0,
      win:  { count: api.wins   || 0, percentage: Math.round(api.winPercentage  || 0) },
      draw: { count: api.draws  || 0, percentage: Math.round(api.drawPercentage || api.drawnPercentage || 0) },
      loss: { count: api.losses || 0, percentage: Math.round(api.lossPercentage || api.lostPercentage  || 0) },
    },
    latestMatchForm: [
      {
        venue:   "Overall",
        results: (api.lmResults || []).map((r) => (r || "").toUpperCase()),
        gpg:     api.lmGPG ?? api.avgGoals ?? 0,
        gd:      goalDiff(api.goalsScored ?? api.scored, api.goalsConceded ?? api.conceded),
      },
      {
        venue:   "Home",
        results: (api.lmResultsHome || []).map((r) => (r || "").toUpperCase()),
        gpg:     api.lmGPGHome ?? api.avgGoalsHome ?? 0,
        gd:      goalDiff(api.goalsScoredHome ?? api.scoredHome, api.goalsConcededHome ?? api.concededHome),
      },
      {
        venue:   "Away",
        results: (api.lmResultsAway || []).map((r) => (r || "").toUpperCase()),
        gpg:     api.lmGPGAway ?? api.avgGoalsAway ?? 0,
        gd:      goalDiff(api.goalsScoredAway ?? api.scoredAway, api.goalsConcededAway ?? api.concededAway),
      },
    ],
    keyStatistics: [
      {
        gameResult: "Overall",
        wins:       api.wins || 0,
        avgGPG:     api.avgGoals || api.avgGoalsPerMatch || 0,
        scored:     api.scored || api.goalsScored || 0,
        conceded:   api.conceded || api.goalsConceded || 0,
        cleanSheets: api.cleanSheets || 0,
      },
      {
        gameResult: "Home",
        wins:       api.wonHome || 0,
        avgGPG:     api.avgGoalsHome || api.avgGoalsPerMatchHome || 0,
        scored:     api.scoredHome || api.goalsScoredHome || 0,
        conceded:   api.concededHome || api.goalsConcededHome || 0,
        cleanSheets: api.cleanSheetsHome || 0,
      },
      {
        gameResult: "Away",
        wins:       api.wonAway || 0,
        avgGPG:     api.avgGoalsAway || api.avgGoalsPerMatchAway || 0,
        scored:     api.scoredAway || api.goalsScoredAway || 0,
        conceded:   api.concededAway || api.goalsConcededAway || 0,
        cleanSheets: api.cleanSheetsAway || 0,
      },
    ],
  };
};

const mapApiTopPlayers = (topStats) => {
  if (!topStats) return [];
  const players = [];
  if (topStats.topAttendance?.player)
    players.push({ name: topStats.topAttendance.player.fullName || "Unknown", stat: "Most appearances", value: topStats.topAttendance.count || 0 });
  if (topStats.topGoals?.player)
    players.push({ name: topStats.topGoals.player.fullName || "Unknown", stat: "Most goals", value: topStats.topGoals.count || 0 });
  if (topStats.topAssists?.player)
    players.push({ name: topStats.topAssists.player.fullName || "Unknown", stat: "Most Assists", value: topStats.topAssists.count || 0 });
  return players;
};

/**
 * TeamOverview Page — admin read-only view of team dashboard
 * Uses the same shared StatisticsTab + PlayersTab as TeamDashboard
 */
const TeamOverview = () => {
  const { teamId }  = useParams();
  const navigate    = useNavigate();
  const { selectedTournament, selectedCategory } = useSelector((s) => s.tournament);

  const [activeTab,   setActiveTab]   = useState("statistics");
  const [teamInfo,    setTeamInfo]    = useState({ name: "Team", crest: null, owner: "" });
  const [players,     setPlayers]     = useState([]);
  const [stats,       setStats]       = useState(null);
  const [topPlayers,  setTopPlayers]  = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      const tournamentId = selectedTournament?.id || selectedTournament?._id;
      const categoryId = selectedCategory?._id || selectedCategory?.id;
      const response = await authApi({
        method: "GET",
        url: `/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/roster`,
      });
      setPlayers(response.data.players || []);
      setTeamInfo({
        name:  response.data.teamName  || "Team",
        crest: response.data.teamCrest,
        owner: response.data.teamOwner || "",
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load team data");
    } finally {
      setLoading(false);
    }
  }, [teamId, selectedTournament, selectedCategory]);

  useEffect(() => { if (teamId) fetchTeamData(); }, [teamId, fetchTeamData]);

  useEffect(() => {
    const fetchStats = async () => {
      const tournamentId = selectedTournament?.id || selectedTournament?._id;
      const categoryId   = selectedCategory?._id  || selectedCategory?.id;
      if (!tournamentId || !categoryId || !teamId) return;
      try {
        // Use plain axios (no logout interceptor) so a 401 is caught locally
        const accessString = window.localStorage.getItem("access");
        const token = accessString ? JSON.parse(accessString).token : null;
        const response = await axios.get(
          `${config.API_BASE_URL}/${config.API_PATH}/tournaments/${tournamentId}/categories/${categoryId}/teams/${teamId}/stats`,
          token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        );
        const mapped = mapApiStatsToUiStats(response.data);
        if (mapped) setStats(mapped);
        const mappedTop = mapApiTopPlayers(response.data?.topStats);
        if (mappedTop.length > 0) setTopPlayers(mappedTop);
      } catch (err) {
      }
    };
    fetchStats();
  }, [teamId, selectedTournament, selectedCategory]);

  /* ── Loading ──────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#00E7FF] animate-spin mx-auto mb-4" />
          <p className="text-app-text-secondary">Loading team data...</p>
        </div>
      </div>
    );
  }

  /* ── Error ────────────────────────────────────────────────────────────── */
  if (error) {
    return (
      <div className="min-h-screen bg-app-bg flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#FF7B7B] mx-auto mb-4" />
          <p className="text-[#FF7B7B] mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-app-card text-app-text rounded-full font-medium text-sm hover:bg-app-card/80 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app-bg">
      <header className="px-6 pt-8 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-app-text-secondary hover:opacity-50 text-sm mb-4 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to participants
        </button>

        {/* Team Info */}
        <div className="flex items-center gap-3">
          <TeamBadge src={teamInfo.crest} alt={teamInfo.name} containerSize="w-12 h-12" />
          <div>
            <h1 className="text-lg font-semibold text-app-text">{teamInfo.name}</h1>
            {teamInfo.owner && <p className="text-xs text-app-text-secondary">Owner: {teamInfo.owner || "—"}</p>}
          </div>
        </div>
      </header>

      <main className="px-6">
        {/* Tab Navigation */}
        <div className="flex items-center gap-3 mb-6">
          <TabButton active={activeTab === "statistics"} onClick={() => setActiveTab("statistics")}>
            Statistics
          </TabButton>
          <TabButton active={activeTab === "players"} onClick={() => setActiveTab("players")}>
            Players
          </TabButton>
        </div>

        {activeTab === "statistics" ? (
          <StatisticsTab
            stats={stats}
            teamName={teamInfo.name}
            hasData={!!stats}
            isReadOnly={true}
          />
        ) : (
          <PlayersTab
            players={players}
            topPlayers={topPlayers}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isReadOnly
          />
        )}
      </main>
    </div>
  );
};

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`h-[40px] px-4 rounded-full text-sm font-medium transition-all ${
      active
        ? "bg-[#001A2A] text-[#00E7FF]"
        : "text-app-text-secondary hover:text-[#00E7FF]"
    }`}
  >
    {children}
  </button>
);

export default TeamOverview;
